import { queryOptions, useQuery } from '@tanstack/react-query';
import ms from 'ms';
import api from '#/api';
import type { RequestOpts } from '#/api/api-types';
import queryClient from '#/api/queryClient';

type GetAllDocumentsQueryParams = {
  filter?: api.DocumentStatusFilter | undefined | null;
};

export type UseAllDocumentsResponse = Awaited<
  ReturnType<typeof api.getDocuments>
>;

export function getAllDocumentsQuery(
  params: GetAllDocumentsQueryParams = {},
  opts: RequestOpts = {},
) {
  const { filter } = params;
  const queryKey = ['getAllDocuments', filter];
  return queryOptions({
    queryKey,
    queryFn: ({ signal }) => {
      if (!filter) {
        throw new TypeError('No filter provided');
      }
      return api.getDocuments(
        {
          filter,
        },
        { ...opts, signal },
      );
    },
    refetchInterval: ms('1m'),
    staleTime: ms('30s'),
    gcTime: ms('5m'),
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    initialDataUpdatedAt: queryClient.getQueryState(queryKey)?.dataUpdatedAt,
  });
}

export async function fetchAllDocumentsQuery(
  params: GetAllDocumentsQueryParams,
  opts: RequestOpts = {},
) {
  return queryClient.fetchQuery(getAllDocumentsQuery(params, opts));
}

export function useAllDocuments(
  params: GetAllDocumentsQueryParams = {},
  options: Omit<ReturnType<typeof getAllDocumentsQuery>, 'queryKey'> = {},
) {
  const query = getAllDocumentsQuery(params);
  return useQuery({
    ...query,
    ...options,
  });
}
